import React, { useState, useEffect } from "react";
import HeroSection from "../components/HeroSection";
import { pagesApi } from "../services/api";
import { config } from "../config/config";

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const [pageData, setPageData] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [contactInfo, setContactInfo] = useState({
    email: "info@innobig.com",
    phone: "+88 (017) 1417-0055",
    address: "",
    response_time: "",
    linkedin_url: "",
    facebook_url: "",
  });

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await pagesApi.getByPageType("contact");
        setPageData(response.data || {});
      } catch (error) {
        console.error("Error loading contact page data:", error);
      }
    };

    fetchPageData();
  }, []);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${config.api.baseURL}/contact/info`);
        if (!response.ok) {
          throw new Error("Failed to load contact info");
        }
        const data = await response.json();
        setContactInfo({
          email: data?.email || "info@innobig.com",
          phone: data?.phone || "+88 (017) 1417-0055",
          address: data?.address || "",
          response_time: data?.response_time || "",
          linkedin_url: data?.linkedin_url || "",
          facebook_url: data?.facebook_url || "",
        });
      } catch (error) {
        console.error("Error loading contact info:", error);
      }
    };

    fetchContactInfo();
  }, []);

  useEffect(() => {
    if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
      }, pageData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [pageData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      const response = await fetch(`${config.api.baseURL}/contact/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.detail || "Failed to send message. Please try again.";
        throw new Error(errorMessage);
      }

      setSubmitStatus({
        type: "success",
        message: "Thank you! Your message has been sent.",
      });
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: error.message || "Failed to send message. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitStatus.message && (
                  <div
                    className={`rounded-lg px-4 py-3 text-sm font-medium ${
                      submitStatus.type === "success"
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-700"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold mb-2">Email</h3>
                  <p className="text-gray-600">{contactInfo.email}</p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Phone</h3>
                  <p className="text-gray-600">{contactInfo.phone}</p>
                </div>
                {contactInfo.address && (
                  <div>
                    <h3 className="font-bold mb-2">Address</h3>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                )}
                <div>
                  <h3 className="font-bold mb-2">Social Media</h3>
                  <div className="flex space-x-4">
                    {contactInfo.linkedin_url && (
                      <a
                        href={contactInfo.linkedin_url}
                        className="text-blue-600 hover:text-blue-800"
                        target="_blank"
                        rel="noreferrer"
                      >
                        LinkedIn
                      </a>
                    )}
                    {contactInfo.facebook_url && (
                      <a
                        href={contactInfo.facebook_url}
                        className="text-blue-600 hover:text-blue-800"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Facebook
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Response Time</h3>
                  <p className="text-gray-600">
                    {contactInfo.response_time ||
                      "We typically respond within 24 hours during business days."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
